import { useState } from "react";
import { EditableField } from "./EditableField";
import { abilityMod, formatMod, proficiencyBonus, rollD20, rollDice } from "./diceUtils";
import { Heart, Shield, Plus, Skull, BookOpenCheck, Swords, Backpack, Award, Trash2 } from "lucide-react";

/**
 * Parchment-themed full character sheet matching the D&D Beyond-style mock.
 *
 *  - Light cream / parchment palette with orange accents — scoped to this component via
 *    inline CSS variables so the surrounding dark Layout chrome is untouched.
 *  - PCs see all three tabs (Combat / Inventory / Features). NPCs see a trimmed sheet
 *    with just the top stat row + ability scores + a slim Bio panel below skills.
 */

const ABILITIES = ["STR", "DEX", "CON", "INT", "WIS", "CHA"];
const ABILITY_LONG = { STR: "Strength", DEX: "Dexterity", CON: "Constitution", INT: "Intelligence", WIS: "Wisdom", CHA: "Charisma" };
const SKILLS_BY_ABILITY = {
  STR: ["Athletics"],
  DEX: ["Acrobatics", "Sleight of Hand", "Stealth"],
  CON: [],
  INT: ["Arcana", "History", "Investigation", "Nature", "Religion"],
  WIS: ["Animal Handling", "Insight", "Medicine", "Perception", "Survival"],
  CHA: ["Deception", "Intimidation", "Performance", "Persuasion"],
};
const SKILL_ORDER = [
  ["Acrobatics","DEX"], ["Animal Handling","WIS"], ["Arcana","INT"], ["Athletics","STR"],
  ["Deception","CHA"], ["History","INT"], ["Insight","WIS"], ["Intimidation","CHA"],
  ["Investigation","INT"], ["Medicine","WIS"], ["Nature","INT"], ["Perception","WIS"],
  ["Performance","CHA"], ["Persuasion","CHA"], ["Religion","INT"], ["Sleight of Hand","DEX"],
  ["Stealth","DEX"], ["Survival","WIS"],
];

const Card = ({ children, className = "", testid }) => (
  <div
    data-testid={testid}
    className={`ps-card rounded-md ${className}`}
    style={{ background: "var(--ps-card)", border: "1px solid var(--ps-border)" }}
  >
    {children}
  </div>
);

const TinyLabel = ({ children, className = "" }) => (
  <span className={`text-[10px] uppercase tracking-[0.15em] ps-muted ${className}`}>{children}</span>
);

const AccentLabel = ({ children, className = "", icon: Icon }) => (
  <div className={`flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold ps-accent ${className}`}>
    {Icon && <Icon className="w-3 h-3" />}
    {children}
  </div>
);

const ToggleDot = ({ on, size = "sm", onClick, testid, title, color = "accent" }) => {
  const s = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
  const fillColor = color === "amber" ? "#d97706" : "var(--ps-accent)";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      title={title}
      className={`${s} rounded-full border-2 flex-shrink-0 transition-colors`}
      style={{
        borderColor: on ? fillColor : "var(--ps-border-strong)",
        background: on ? fillColor : "transparent",
      }}
    />
  );
};

const NumberBox = ({ value, onSave, testid, type = "number", className = "", inputClassName = "" }) => (
  <div className="ps-input rounded-sm flex items-center justify-center" style={{ background: "var(--ps-input)", border: "1px solid var(--ps-border)" }}>
    <EditableField
      value={value ?? ""}
      type={type}
      onSave={onSave}
      className={`text-center w-full ${className}`}
      inputClassName={`text-center bg-transparent ${inputClassName}`}
      data-testid={testid}
    />
  </div>
);

export const ParchmentSheet = ({ character, sheet, updateSheet, updateCharacter, mode = "pc" }) => {
  const [activeTab, setActiveTab] = useState("combat");

  const level = character.level || 1;
  const profBonus = sheet.proficiency_bonus ?? proficiencyBonus(level);
  const abilities = sheet.abilities || {};
  const savingProfs = sheet.saving_throws || [];
  const skillProfs = sheet.skill_proficiencies || [];
  const skillExpertise = sheet.skill_expertise || [];

  const setAbility = (ab, val) => updateSheet({ abilities: { ...abilities, [ab]: parseInt(val, 10) || 10 } });
  const toggleSave = (ab) => {
    const next = savingProfs.includes(ab) ? savingProfs.filter(x => x !== ab) : [...savingProfs, ab];
    updateSheet({ saving_throws: next });
  };
  const toggleSkill = (skill) => {
    const next = skillProfs.includes(skill) ? skillProfs.filter(x => x !== skill) : [...skillProfs, skill];
    updateSheet({ skill_proficiencies: next });
  };
  const toggleExpertise = (skill) => {
    const next = skillExpertise.includes(skill) ? skillExpertise.filter(x => x !== skill) : [...skillExpertise, skill];
    updateSheet({ skill_expertise: next });
  };
  const skillMod = (ab, skill) => {
    const base = abilityMod(abilities[ab] ?? 10);
    let bonus = 0;
    if (skillProfs.includes(skill)) bonus += profBonus;
    if (skillExpertise.includes(skill)) bonus += profBonus;
    return base + bonus;
  };
  const saveMod = (ab) => abilityMod(abilities[ab] ?? 10) + (savingProfs.includes(ab) ? profBonus : 0);

  const passivePerception   = 10 + skillMod("WIS", "Perception");
  const passiveInvestigation = 10 + skillMod("INT", "Investigation");
  const passiveInsight      = 10 + skillMod("WIS", "Insight");

  const currentHp = sheet.current_hp ?? sheet.max_hp ?? 0;
  const maxHp = sheet.max_hp ?? 0;
  const tempHp = sheet.temp_hp ?? 0;
  const hpPct = maxHp > 0 ? Math.max(0, Math.min(100, (currentHp / maxHp) * 100)) : 0;

  const ds = sheet.death_saves || { successes: 0, failures: 0 };
  const setDS = (k, v) => updateSheet({ death_saves: { ...ds, [k]: v } });

  const actions = sheet.tracked_actions || [];
  const addAction = () => {
    const name = window.prompt("Action name (e.g. Longsword Slash, Eldritch Blast)?");
    if (!name) return;
    updateSheet({ tracked_actions: [...actions, { id: Date.now().toString(36), name, notes: "" }] });
  };
  const removeAction = (id) => updateSheet({ tracked_actions: actions.filter(a => a.id !== id) });

  const inventory = sheet.inventory || {};
  const equipment = inventory.equipment || [];
  const magicalItems = inventory.magical_items || [];
  const currency = inventory.currency || { cp:0, sp:0, ep:0, gp:0, pp:0 };
  const features = sheet.features || { racial: [], class: [], feats: [], passive: [] };

  // Parchment palette — scoped CSS variables. The surrounding Layout stays dark.
  const parchmentVars = {
    "--ps-bg": "#efe3c8",
    "--ps-card": "#faf2dc",
    "--ps-input": "#fffaeb",
    "--ps-border": "#d4c4a5",
    "--ps-border-strong": "#a99875",
    "--ps-text": "#3e2c1c",
    "--ps-muted": "#7a6a52",
    "--ps-accent": "#c4661f",
    "--ps-accent-strong": "#a64f0e",
    "--ps-hp-bar": "#7cb342",
  };

  const showTabs = mode !== "npc"; // NPCs get a trimmed view (no tabs panel)

  return (
    <div
      data-testid="parchment-sheet"
      data-mode={mode}
      className="rounded-md p-4 md:p-6"
      style={{
        ...parchmentVars,
        background: "var(--ps-bg)",
        color: "var(--ps-text)",
        backgroundImage:
          "radial-gradient(circle at 20% 0%, rgba(255,255,255,0.4), transparent 60%), " +
          "radial-gradient(circle at 100% 100%, rgba(0,0,0,0.05), transparent 60%)",
      }}
    >
      {/* ===== TOP ROW — Class card · HP card · AC/Speed/Init card ===== */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-4">
        {/* Class / Race / Proficiency / Inspiration */}
        <Card testid="ps-class-card" className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <EditableField
                value={character.character_class || ""}
                placeholder="Class"
                onSave={(v) => updateCharacter({ character_class: v })}
                className="font-serif text-2xl"
                inputClassName="h-9 text-xl font-serif bg-transparent"
                data-testid="ps-class-name"
              />
            </div>
            <NumberBox
              value={level}
              onSave={(v) => updateCharacter({ level: parseInt(v, 10) || 1 })}
              testid="ps-level"
              className="font-serif text-xl tabular-nums"
              inputClassName="w-12 h-10 text-xl font-serif"
            />
          </div>
          <div className="mt-2">
            <EditableField
              value={character.race || ""}
              placeholder="Race"
              onSave={(v) => updateCharacter({ race: v })}
              className="text-lg"
              inputClassName="h-7 text-base bg-transparent"
              data-testid="ps-race"
            />
          </div>
          <div className="mt-4 flex items-center justify-between text-xs">
            <div>
              <TinyLabel>Proficiency</TinyLabel>{" "}
              <span className="ps-accent font-bold tabular-nums" style={{ color: "var(--ps-accent)" }}>
                {formatMod(profBonus)}
              </span>
            </div>
            <button
              className="flex items-center gap-1.5"
              onClick={() => updateSheet({ inspiration: !sheet.inspiration })}
              data-testid="ps-inspiration-toggle"
            >
              <ToggleDot on={!!sheet.inspiration} color="amber" />
              <TinyLabel>Inspiration</TinyLabel>
            </button>
          </div>
        </Card>

        {/* HP card */}
        <Card testid="ps-hp-card" className="p-4">
          <AccentLabel icon={Heart}>Hit Points</AccentLabel>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <div>
              <NumberBox value={currentHp} onSave={(v) => updateSheet({ current_hp: parseInt(v, 10) || 0 })} testid="ps-hp-current" className="text-2xl font-serif" inputClassName="text-2xl font-serif w-full h-10" />
              <TinyLabel className="block text-center mt-1">Current</TinyLabel>
            </div>
            <div>
              <NumberBox value={maxHp} onSave={(v) => {
                const nm = parseInt(v, 10) || 0;
                const patch = { max_hp: nm };
                if (currentHp > nm) patch.current_hp = nm;
                updateSheet(patch);
              }} testid="ps-hp-max" className="text-2xl font-serif" inputClassName="text-2xl font-serif w-full h-10" />
              <TinyLabel className="block text-center mt-1">Max</TinyLabel>
            </div>
            <div>
              <NumberBox value={tempHp} onSave={(v) => updateSheet({ temp_hp: parseInt(v, 10) || 0 })} testid="ps-hp-temp" className="text-2xl font-serif" inputClassName="text-2xl font-serif w-full h-10" />
              <TinyLabel className="block text-center mt-1">Temp</TinyLabel>
            </div>
          </div>
          {/* HP bar */}
          <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--ps-border)" }}>
            <div className="h-full transition-all" style={{ width: `${hpPct}%`, background: "var(--ps-hp-bar)" }} data-testid="ps-hp-bar" />
          </div>
          {/* Hit Dice row */}
          <div className="mt-3 flex items-center gap-2 text-sm">
            <TinyLabel>Hit Dice</TinyLabel>
            <input
              type="text"
              defaultValue={sheet.hit_dice_type || "d10"}
              onBlur={(e) => updateSheet({ hit_dice_type: e.target.value })}
              className="rounded-sm px-2 py-0.5 w-14 text-sm text-center"
              style={{ background: "var(--ps-input)", border: "1px solid var(--ps-border)", color: "var(--ps-text)" }}
              data-testid="ps-hit-dice-type"
            />
            <NumberBox value={sheet.hit_dice_remaining ?? (sheet.hit_dice_total || level)} onSave={(v) => updateSheet({ hit_dice_remaining: parseInt(v, 10) || 0 })} testid="ps-hit-dice-remaining" className="text-sm" inputClassName="w-10 h-7 text-center text-sm" />
            <span style={{ color: "var(--ps-muted)" }}>/</span>
            <NumberBox value={sheet.hit_dice_total || level} onSave={(v) => updateSheet({ hit_dice_total: parseInt(v, 10) || 1 })} testid="ps-hit-dice-total" className="text-sm" inputClassName="w-10 h-7 text-center text-sm" />
          </div>
        </Card>

        {/* AC / Speed / Initiative card */}
        <Card testid="ps-ac-card" className="p-4">
          <div className="flex items-center justify-between">
            <AccentLabel icon={Shield}>Armor Class</AccentLabel>
            <NumberBox value={sheet.armor_class ?? character.armor_class ?? 10} onSave={(v) => updateSheet({ armor_class: parseInt(v, 10) || 10 })} testid="ps-ac" className="text-2xl font-serif w-16" inputClassName="w-16 h-10 text-2xl font-serif" />
          </div>
          <div className="mt-3 pt-3 border-t flex items-center justify-between text-sm" style={{ borderColor: "var(--ps-border)" }}>
            <TinyLabel>Speed</TinyLabel>
            <div className="flex items-center gap-1">
              <NumberBox value={String(sheet.speed ?? character.speed ?? 30).replace(/\s*ft\.?$/i, "")} onSave={(v) => updateSheet({ speed: parseInt(v, 10) || 30 })} testid="ps-speed" className="text-lg font-serif" inputClassName="w-14 h-7 text-center text-lg font-serif" />
              <span className="text-xs" style={{ color: "var(--ps-muted)" }}>ft</span>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between text-sm">
            <TinyLabel>Initiative</TinyLabel>
            <button
              onClick={() => rollD20(abilityMod(abilities.DEX ?? 10), "Initiative")}
              className="text-lg font-serif tabular-nums hover:opacity-70"
              style={{ color: "var(--ps-text)" }}
              data-testid="ps-init"
            >
              {formatMod(abilityMod(abilities.DEX ?? 10))}
            </button>
          </div>
        </Card>
      </div>

      {/* ===== ABILITY SCORES ROW ===== */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3 mb-4">
        {ABILITIES.map(ab => {
          const score = abilities[ab] ?? 10;
          const mod = abilityMod(score);
          const sv = saveMod(ab);
          const isSave = savingProfs.includes(ab);
          return (
            <Card key={ab} testid={`ps-ability-${ab.toLowerCase()}`} className="p-3 text-center">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-bold tracking-widest" style={{ color: "var(--ps-accent)" }}>{ab}</span>
                <NumberBox value={score} onSave={(v) => setAbility(ab, v)} testid={`ps-ability-${ab.toLowerCase()}-score`} className="text-sm w-10" inputClassName="w-12 h-7 text-center text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-1">
                <button onClick={() => rollD20(mod, `${ABILITY_LONG[ab]} check`)} className="rounded-sm py-1 hover:opacity-70" data-testid={`ps-ability-${ab.toLowerCase()}-mod`}>
                  <div className="font-serif text-2xl tabular-nums leading-none">{formatMod(mod)}</div>
                  <TinyLabel className="block mt-1">Mod</TinyLabel>
                </button>
                <button onClick={() => { if (!isSave) toggleSave(ab); rollD20(sv, `${ABILITY_LONG[ab]} save`); }} onContextMenu={(e) => { e.preventDefault(); toggleSave(ab); }} className="rounded-sm py-1 hover:opacity-70 relative" data-testid={`ps-ability-${ab.toLowerCase()}-save`}>
                  <div className="font-serif text-2xl tabular-nums leading-none" style={{ color: isSave ? "var(--ps-accent)" : "var(--ps-text)" }}>{formatMod(sv)}</div>
                  <TinyLabel className="block mt-1">Save</TinyLabel>
                  {isSave && <span className="absolute top-0 right-0 w-1.5 h-1.5 rounded-full" style={{ background: "var(--ps-accent)" }} />}
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* ===== BOTTOM SPLIT — Skills sidebar  +  Tabs (Combat / Inventory / Features) ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* SKILLS sidebar */}
        <Card testid="ps-skills" className={`p-4 ${showTabs ? "lg:col-span-4" : "lg:col-span-12"}`}>
          <AccentLabel className="mb-2"><span className="inline-block w-3 h-3 rounded-full mr-1" style={{ background: "var(--ps-accent)" }} />Skills</AccentLabel>
          <ul className="space-y-1 text-sm">
            {SKILL_ORDER.map(([skill, ab]) => {
              const prof = skillProfs.includes(skill);
              const exp = skillExpertise.includes(skill);
              const mod = skillMod(ab, skill);
              const slug = skill.toLowerCase().replace(/\s+/g, "-");
              return (
                <li key={skill} className="flex items-center gap-2" data-testid={`ps-skill-${slug}`}>
                  <ToggleDot
                    on={prof || exp}
                    color={exp ? "amber" : "accent"}
                    onClick={() => toggleSkill(skill)}
                    title={exp ? "Expertise (right-click toggle)" : prof ? "Proficient (right-click for expertise)" : "Click to mark proficient"}
                    testid={`ps-skill-toggle-${slug}`}
                  />
                  <button
                    onClick={() => rollD20(mod, skill)}
                    onContextMenu={(e) => { e.preventDefault(); toggleExpertise(skill); }}
                    className="font-serif text-sm tabular-nums w-9 text-left hover:opacity-70"
                    data-testid={`ps-skill-roll-${slug}`}
                    style={{ color: "var(--ps-accent)" }}
                  >{formatMod(mod)}</button>
                  <span className="flex-1 truncate">{skill}</span>
                  <span className="text-[10px] tracking-wider" style={{ color: "var(--ps-muted)" }}>{ab}</span>
                </li>
              );
            })}
          </ul>
          {/* Passives */}
          <div className="mt-4 pt-3 border-t space-y-1 text-sm" style={{ borderColor: "var(--ps-border)" }}>
            {[
              ["Passive Perception", passivePerception],
              ["Passive Investigation", passiveInvestigation],
              ["Passive Insight", passiveInsight],
            ].map(([label, v]) => (
              <div key={label} className="flex justify-between" data-testid={`ps-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <span style={{ color: "var(--ps-muted)" }}>{label}</span>
                <span className="font-bold tabular-nums">{v}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* TABS — Combat / Inventory / Features */}
        {showTabs && (
          <div className="lg:col-span-8">
            <div className="flex gap-1 mb-2" data-testid="ps-tab-bar">
              {[
                { id: "combat", label: "Combat", Icon: Swords },
                { id: "inventory", label: "Inventory", Icon: Backpack },
                { id: "features", label: "Features", Icon: Award },
              ].map(({ id, label, Icon }) => {
                const active = activeTab === id;
                return (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    data-testid={`ps-tab-${id}`}
                    className="px-3 py-2 flex items-center gap-1.5 text-sm uppercase tracking-wider font-bold rounded-t-sm transition-colors"
                    style={{
                      color: active ? "var(--ps-accent)" : "var(--ps-muted)",
                      borderBottom: active ? "2px solid var(--ps-accent)" : "2px solid transparent",
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </button>
                );
              })}
            </div>

            {activeTab === "combat" && (
              <Card testid="ps-tab-combat" className="p-4 space-y-5">
                {/* Hit Dice */}
                <div data-testid="ps-combat-hit-dice">
                  <AccentLabel className="mb-2"><span className="font-serif">⚄</span> Hit Dice</AccentLabel>
                  <div className="flex items-center gap-3">
                    <div>
                      <input
                        type="text"
                        defaultValue={sheet.hit_dice_type || "d10"}
                        onBlur={(e) => updateSheet({ hit_dice_type: e.target.value })}
                        className="rounded-sm px-2 py-1 w-16 text-center font-serif text-base"
                        style={{ background: "var(--ps-input)", border: "1px solid var(--ps-border)", color: "var(--ps-text)" }}
                        data-testid="ps-combat-hit-dice-type"
                      />
                      <TinyLabel className="block text-center mt-1">Type</TinyLabel>
                    </div>
                    <div>
                      <NumberBox value={sheet.hit_dice_remaining ?? level} onSave={(v) => updateSheet({ hit_dice_remaining: parseInt(v, 10) || 0 })} testid="ps-combat-hit-dice-remaining" className="text-base font-serif" inputClassName="w-12 h-8 text-center font-serif text-base" />
                      <TinyLabel className="block text-center mt-1">Remaining</TinyLabel>
                    </div>
                    <span style={{ color: "var(--ps-muted)" }}>/</span>
                    <div>
                      <NumberBox value={sheet.hit_dice_total || level} onSave={(v) => updateSheet({ hit_dice_total: parseInt(v, 10) || 1 })} testid="ps-combat-hit-dice-total" className="text-base font-serif" inputClassName="w-12 h-8 text-center font-serif text-base" />
                      <TinyLabel className="block text-center mt-1">Total</TinyLabel>
                    </div>
                    <button onClick={() => rollDice(`1${sheet.hit_dice_type || "d10"}`, "Hit Die")} className="ml-auto text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm hover:opacity-80" style={{ background: "var(--ps-accent)", color: "white" }} data-testid="ps-combat-roll-hit-die">Roll</button>
                  </div>
                </div>

                {/* Death Saves */}
                <div data-testid="ps-combat-death-saves">
                  <AccentLabel icon={Skull} className="mb-2">Death Saves</AccentLabel>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <TinyLabel>Successes</TinyLabel>
                      <div className="flex gap-1">
                        {[1,2,3].map(i => (
                          <ToggleDot key={`s${i}`} on={ds.successes >= i} color="accent" onClick={() => setDS("successes", ds.successes === i ? i - 1 : i)} testid={`ps-death-success-${i}`} />
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <TinyLabel>Failures</TinyLabel>
                      <div className="flex gap-1">
                        {[1,2,3].map(i => (
                          <ToggleDot key={`f${i}`} on={ds.failures >= i} color="amber" onClick={() => setDS("failures", ds.failures === i ? i - 1 : i)} testid={`ps-death-failure-${i}`} />
                        ))}
                      </div>
                    </div>
                    {(ds.successes || ds.failures) ? (
                      <button onClick={() => updateSheet({ death_saves: { successes: 0, failures: 0 } })} className="text-[11px] underline ml-auto" style={{ color: "var(--ps-muted)" }} data-testid="ps-death-reset">Reset</button>
                    ) : null}
                  </div>
                </div>

                {/* Quick Reference */}
                <div data-testid="ps-combat-quick-ref">
                  <AccentLabel icon={BookOpenCheck} className="mb-2">Quick Reference</AccentLabel>
                  <div className="flex flex-wrap gap-6 items-end">
                    <div><div className="font-serif text-2xl tabular-nums leading-none">{sheet.armor_class ?? character.armor_class ?? 10}</div><TinyLabel>AC</TinyLabel></div>
                    <div><div className="font-serif text-2xl tabular-nums leading-none">{formatMod(abilityMod(abilities.DEX ?? 10))}</div><TinyLabel>Initiative</TinyLabel></div>
                    <div><div className="font-serif text-2xl tabular-nums leading-none">{String(sheet.speed ?? character.speed ?? 30).replace(/\s*ft\.?$/i, "")} ft</div><TinyLabel>Speed</TinyLabel></div>
                  </div>
                </div>

                {/* Actions */}
                <div data-testid="ps-combat-actions">
                  <AccentLabel icon={Swords} className="mb-2">Actions</AccentLabel>
                  <div className="rounded-sm" style={{ background: "var(--ps-input)", border: "1px solid var(--ps-border)" }}>
                    <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderColor: "var(--ps-border)" }}>
                      <TinyLabel>Tracked</TinyLabel>
                      <span className="text-sm font-bold tabular-nums">{actions.length}</span>
                    </div>
                    {actions.length === 0 ? (
                      <div className="px-3 py-8 text-center text-sm" style={{ color: "var(--ps-muted)" }}>
                        No actions recorded. Note down attacks, special moves, signature combos, or anything you want at your fingertips mid-fight.
                      </div>
                    ) : (
                      <ul className="divide-y" style={{ borderColor: "var(--ps-border)" }}>
                        {actions.map(a => (
                          <li key={a.id} className="px-3 py-2 flex items-center gap-2 text-sm" data-testid={`ps-action-${a.id}`}>
                            <span className="flex-1 font-serif">{a.name}</span>
                            {a.notes && <span className="text-xs italic" style={{ color: "var(--ps-muted)" }}>{a.notes}</span>}
                            <button onClick={() => removeAction(a.id)} className="opacity-50 hover:opacity-100" data-testid={`ps-action-remove-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <button onClick={addAction} className="w-full px-3 py-2 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 hover:opacity-80" style={{ color: "var(--ps-accent)", borderTop: "1px solid var(--ps-border)" }} data-testid="ps-action-add">
                      <Plus className="w-3.5 h-3.5" /> Add Action
                    </button>
                  </div>
                </div>
              </Card>
            )}

            {activeTab === "inventory" && (
              <Card testid="ps-tab-inventory" className="p-4 space-y-4">
                {/* Currency */}
                <div data-testid="ps-inv-currency">
                  <AccentLabel className="mb-2">Currency</AccentLabel>
                  <div className="grid grid-cols-5 gap-2">
                    {["pp","gp","ep","sp","cp"].map(coin => (
                      <div key={coin} className="text-center">
                        <NumberBox value={currency[coin] ?? 0} onSave={(v) => updateSheet({ inventory: { ...inventory, currency: { ...currency, [coin]: parseInt(v, 10) || 0 } } })} testid={`ps-currency-${coin}`} className="text-base font-serif" inputClassName="w-full h-8 text-center font-serif text-base" />
                        <TinyLabel className="block mt-1">{coin.toUpperCase()}</TinyLabel>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Equipment */}
                <div data-testid="ps-inv-equipment">
                  <AccentLabel className="mb-2">Equipment</AccentLabel>
                  {equipment.length === 0 ? (
                    <p className="text-sm py-3 text-center" style={{ color: "var(--ps-muted)" }}>No equipment yet</p>
                  ) : (
                    <ul className="space-y-1 text-sm">
                      {equipment.map((it, idx) => (
                        <li key={idx} className="flex items-center justify-between border-b py-1" style={{ borderColor: "var(--ps-border)" }}>
                          <span>{it.name}</span>
                          {it.quantity > 1 && <span className="text-xs" style={{ color: "var(--ps-muted)" }}>×{it.quantity}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                  <button onClick={() => {
                    const name = window.prompt("Equipment name?");
                    if (!name) return;
                    updateSheet({ inventory: { ...inventory, equipment: [...equipment, { name, quantity: 1 }] } });
                  }} className="mt-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1" style={{ color: "var(--ps-accent)" }} data-testid="ps-inv-equipment-add"><Plus className="w-3 h-3" /> Add Item</button>
                </div>
                {/* Magical Items */}
                <div data-testid="ps-inv-magical">
                  <AccentLabel className="mb-2">Magical Items</AccentLabel>
                  {magicalItems.length === 0 ? (
                    <p className="text-sm py-3 text-center" style={{ color: "var(--ps-muted)" }}>No magical items</p>
                  ) : (
                    <ul className="space-y-1 text-sm">
                      {magicalItems.map((it, idx) => (
                        <li key={idx} className="flex items-center justify-between border-b py-1" style={{ borderColor: "var(--ps-border)" }}>
                          <span>{it.name}</span>
                          {it.attuned && <span className="text-[10px] uppercase tracking-wider" style={{ color: "var(--ps-accent)" }}>Attuned</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                  <button onClick={() => {
                    const name = window.prompt("Magical item name?");
                    if (!name) return;
                    updateSheet({ inventory: { ...inventory, magical_items: [...magicalItems, { name, attuned: false }] } });
                  }} className="mt-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1" style={{ color: "var(--ps-accent)" }} data-testid="ps-inv-magical-add"><Plus className="w-3 h-3" /> Add Magical Item</button>
                </div>
              </Card>
            )}

            {activeTab === "features" && (
              <Card testid="ps-tab-features" className="p-4 space-y-4">
                {[
                  ["racial", "Racial Traits"],
                  ["class", "Class Features"],
                  ["feats", "Feats"],
                  ["passive", "Passive Bonuses"],
                ].map(([key, label]) => (
                  <div key={key} data-testid={`ps-features-${key}`}>
                    <AccentLabel className="mb-2">{label}</AccentLabel>
                    {(features[key] || []).length === 0 ? (
                      <p className="text-sm py-2" style={{ color: "var(--ps-muted)" }}>No {label.toLowerCase()} recorded</p>
                    ) : (
                      <ul className="space-y-2 text-sm">
                        {(features[key] || []).map((f, idx) => (
                          <li key={idx} className="border-l-2 pl-3 py-1" style={{ borderColor: "var(--ps-accent)" }}>
                            <p className="font-bold font-serif">{f.name}</p>
                            {f.description && <p className="text-xs" style={{ color: "var(--ps-muted)" }}>{f.description}</p>}
                          </li>
                        ))}
                      </ul>
                    )}
                    <button onClick={() => {
                      const name = window.prompt(`${label} — name?`);
                      if (!name) return;
                      const description = window.prompt("Description (optional)") || "";
                      updateSheet({ features: { ...features, [key]: [...(features[key] || []), { name, description }] } });
                    }} className="mt-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1" style={{ color: "var(--ps-accent)" }} data-testid={`ps-features-${key}-add`}><Plus className="w-3 h-3" /> Add</button>
                  </div>
                ))}
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParchmentSheet;
